// [PURPOSE]: Pure helper for the character-creation wizard equipment grant.
// [WHY]: Before this helper, handleCharacterWizardComplete (App.jsx) ran a
//        bare grid-placement loop that pushed every InventoryItem into the
//        inventory as its own entry — even if the item was stackable and an
//        identical stack already existed. As a result, two daggers from the
//        wizard landed as TWO 1×2 stacks instead of merging into one stack of
//        quantity 2 (P2.14 from the audit), and re-running the wizard /
//        re-importing starter equipment created parallel stacks indefinitely.
//
// [INTEGRATION]: Used inside the single setInventory() functional updater in
//        handleCharacterWizardComplete so that ALL incoming items are merged
//        and placed in one render. Mirrors InventoryContext.addItem's stackable
//        rule (`name && category` match → top up quantity) so the wizard and
//        per-item adds behave consistently.
//
// [WHY NOT just call addItem in a loop?]: addItem fires its own
//        setInventory() call per item; looping it inside another setInventory
//        would either schedule N async updates against stale state (the exact
//        race condition the Nov 25 2025 rewrite of this function fixed) or
//        re-introduce duplicate stacks via stale closures. The pure helper
//        keeps the single-batch contract and re-uses addItem's merge rule.

/**
 * Merge stackable items into existing stacks where possible, otherwise place
 * them on the inventory grid using collision-aware scanning.
 *
 * The function is pure — it does NOT mutate `existingInventory` or any item in
 * `incomingItems`. New entries returned in `newInventory` are fresh shallow
 * copies so callers can use them directly inside a setState updater.
 *
 * @param {Array<Object>} incomingItems - Items to add. Each must have:
 *   - name: string
 *   - category: string
 *   - stackable: boolean
 *   - quantity: number (defaults to 1 if missing)
 *   - size: [width, height]
 *   - position: [x, y] | null  (if pre-set, placement honors it)
 * @param {Array<Object>} existingInventory - Current inventory list.
 * @param {number} gridCols - Bag width.
 * @param {number} gridRows - Bag height.
 * @returns {{
 *   newInventory: Array<Object>,
 *   itemsNotAdded: Array<string>,
 *   addedItems: Array<{
 *     item: Object,           // The resulting inventory entry (merged or placed)
 *     incoming: Object,       // The original incoming item (for caller context)
 *     stacked: boolean,       // True when the incoming item topped up an existing stack
 *     placed: boolean,        // True when the incoming item landed on the grid (or was honored at its pre-set position)
 *     deltaQuantity: number,  // Quantity contributed by this incoming item
 *   }>
 * }}
 */
export function mergeAndPlaceWizardItems(incomingItems, existingInventory, gridCols, gridRows) {
  const newInventory = (existingInventory || []).map(item => ({ ...item }));
  const itemsNotAdded = [];
  const addedItems = [];

  (incomingItems || []).forEach(incoming => {
    if (!incoming) return;

    const qty = incoming.quantity || 1;

    // [STACK MERGE]: Mirror InventoryContext.addItem's rule — if the incoming
    // item is stackable AND another item in the inventory shares both name AND
    // category, top up the existing stack instead of creating a new entry.
    if (incoming.stackable) {
      const existingIndex = newInventory.findIndex(existing =>
        existing.name === incoming.name && existing.category === incoming.category
      );
      if (existingIndex !== -1) {
        const existing = newInventory[existingIndex];
        const merged = {
          ...existing,
          quantity: (existing.quantity || 1) + qty,
        };
        newInventory[existingIndex] = merged;
        addedItems.push({
          item: merged,
          incoming,
          stacked: true,
          placed: false,
          deltaQuantity: qty,
        });
        return;
      }
    }

    // [POSITION HONORING]: If the caller has already chosen a position (e.g.
    // a wizard step that pre-placed an item), keep it.
    if (Array.isArray(incoming.position) && incoming.position.length === 2) {
      const placedItem = { ...incoming };
      newInventory.push(placedItem);
      addedItems.push({
        item: placedItem,
        incoming,
        stacked: false,
        placed: true,
        deltaQuantity: qty,
      });
      return;
    }

    // [GRID SCAN]: Fall back to the same collision-checking placement loop the
    // wizard previously had inline. Keeps prior behavior for fresh placements.
    const size = incoming.size || [1, 1];
    let placed = false;

    for (let y = 0; y <= gridRows - size[1] && !placed; y++) {
      for (let x = 0; x <= gridCols - size[0] && !placed; x++) {
        const occupied = newInventory.some(existing => {
          if (!existing.position) return false;
          const [ex, ey] = existing.position;
          const eSize = existing.size || [1, 1];
          for (let cx = x; cx < x + size[0]; cx++) {
            for (let cy = y; cy < y + size[1]; cy++) {
              if (cx >= ex && cx < ex + eSize[0] && cy >= ey && cy < ey + eSize[1]) {
                return true;
              }
            }
          }
          return false;
        });

        if (!occupied) {
          const placedItem = { ...incoming, position: [x, y] };
          newInventory.push(placedItem);
          addedItems.push({
            item: placedItem,
            incoming,
            stacked: false,
            placed: true,
            deltaQuantity: qty,
          });
          placed = true;
        }
      }
    }

    if (!placed) {
      itemsNotAdded.push(incoming.name);
    }
  });

  return { newInventory, itemsNotAdded, addedItems };
}
